import { DateTime } from '@ksyos/date-time';
import {
    Card,
    Checkbox,
    Col,
    Form,
    FormButtonRow,
    FormErrorMessage,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
    Row,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { DayOfWeek } from '@common/enums/';
import type { ValidationErrors } from '@frontend/utils/validation';
import { AvailabilityType, BusinessDays } from '@common/enums/';
import {
    availabilityAddEditSchema,
    validateAvailabilityPeriod,
    validateAvailabilityPeriodOverlap,
} from '@common/validation/availability';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { scrollToValidationErrorByClassName } from '@frontend/utils';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { IAvailability } from '../../../interfaces/availability';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { SaveButton } from '../../../userDataManagement/SaveButton';

interface IAvailabilitiesByDay {
    [day: string]: {
        periodOne: IAvailability;
        periodTwo: IAvailability;
    };
}

export interface IGgzAvailabilitySettingsProps {
    employmentId: string;
    isReadOnly: boolean;
}

export function GgzAvailabilitySettings(props: IGgzAvailabilitySettingsProps) {
    const defaultStartTimePeriodOne = '09:00';
    const defaultEndTimePeriodOne = '12:00';
    const defaultStartTimePeriodTwo = '13:00';
    const defaultEndTimePeriodTwo = '17:00';

    const [canAdd, setCanAdd] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [originalAvailabilities, setOriginalAvailabilities] = useState<IAvailabilitiesByDay>();
    const [availabilities, setAvailabilities] = useState<IAvailabilitiesByDay>();
    const [scrollToErrorSignalCounter, setScrollToErrorSignalCounter] = useState(0);

    useEffect(() => {
        loadAvailabilities();
    }, []);

    useEffect(() => {
        if (scrollToErrorSignalCounter > 0) {
            scrollToValidationErrorByClassName('availability-form');
        }
    }, [scrollToErrorSignalCounter]);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    props.isReadOnly || isEditing ? undefined : canAdd ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : undefined
                }
                title={translateKey('personalSettings.ggz.phoneAvailability')}
            />
            <Card.Body>
                <Form
                    className="availability-form"
                    submitOnEnter={false}
                >
                    {availabilities &&
                        Object.keys(availabilities).map((day) => (
                            <FormSection
                                key={day}
                                title={translateKey(`daysEnum.${day}`)}
                            >
                                {renderAvailabilityPeriod(day as DayOfWeek, 'periodOne', availabilities[day].periodOne)}
                                {renderAvailabilityPeriod(day as DayOfWeek, 'periodTwo', availabilities[day].periodTwo)}

                                {errors.get(`availabilities.${day}`) && (
                                    <FormGroup>
                                        <FormErrorMessage>
                                            {getValidationErrorTranslation(errors.get(`availabilities.${day}`))}
                                        </FormErrorMessage>
                                    </FormGroup>
                                )}
                            </FormSection>
                        ))}

                    {isEditing && (
                        <FormButtonRow>
                            <CancelButton
                                disabled={isSaving}
                                onCancel={async () => handleCancel()}
                            />
                            <SaveButton
                                disabled={isSaving}
                                onSave={handleSubmit}
                            />
                        </FormButtonRow>
                    )}
                </Form>
            </Card.Body>
        </Card>
    );

    function renderAvailabilityPeriod(day: DayOfWeek, period: 'periodOne' | 'periodTwo', availability: IAvailability) {
        return (
            (isEditing || availability.enabled || period === 'periodOne') && (
                <FormGroup
                    key={`${day}_period_${period}`}
                    label={translateKey('personalSettings.ggz.availability')}
                >
                    <Row columnGap={4}>
                        {isEditing && (
                            <Col>
                                <Checkbox
                                    checked={availability.enabled}
                                    onChange={() => toggleTimePeriodEnabled(day, period, !availability.enabled)}
                                    sx={{ mt: 2 }}
                                />
                            </Col>
                        )}
                        {(isEditing || availability.enabled) && (
                            <>
                                <Col>
                                    {isEditing ? (
                                        <TextInput
                                            error={getValidationErrorTranslation(
                                                errors.get(`availabilities.${day}.${period}.startTime`)
                                            )}
                                            onBlur={(e) =>
                                                handleTimeInputBlur(day, period, 'startTime', e.target.value)
                                            }
                                            onChange={(e) => {
                                                handleChange(day, period, { startTime: e.target.value });
                                                removePeriodErrors(day);
                                            }}
                                            onClick={() => toggleTimePeriodEnabled(day, period, true)}
                                            placeholder={
                                                period === 'periodOne'
                                                    ? defaultStartTimePeriodOne
                                                    : defaultStartTimePeriodTwo
                                            }
                                            style={{ textAlign: 'right' }}
                                            sx={{ width: '80px' }}
                                            value={
                                                availability.enabled ? availability.startTime! : isEditing ? '' : '-'
                                            }
                                        />
                                    ) : (
                                        <FormParagraph>
                                            {availability.enabled ? availability.startTime! : isEditing ? '' : '-'}
                                        </FormParagraph>
                                    )}
                                </Col>
                                <Col>
                                    <Text
                                        as="p"
                                        size="md"
                                        sx={{ mt: isEditing ? 2 : [0, null, 1] }}
                                    >
                                        t/m
                                    </Text>
                                </Col>
                                <Col>
                                    {isEditing ? (
                                        <TextInput
                                            error={getValidationErrorTranslation(
                                                errors.get(`availabilities.${day}.${period}.endTime`)
                                            )}
                                            onBlur={(e) => handleTimeInputBlur(day, period, 'endTime', e.target.value)}
                                            onChange={(e) => {
                                                handleChange(day, period, { endTime: e.target.value });
                                                removePeriodErrors(day);
                                            }}
                                            onClick={() => toggleTimePeriodEnabled(day, period, true)}
                                            placeholder={
                                                period === 'periodOne'
                                                    ? defaultEndTimePeriodOne
                                                    : defaultEndTimePeriodTwo
                                            }
                                            style={{ textAlign: 'right' }}
                                            sx={{ width: '80px' }}
                                            value={availability.enabled ? availability.endTime! : isEditing ? '' : '-'}
                                        />
                                    ) : (
                                        <FormParagraph>
                                            {availability.enabled ? availability.endTime! : isEditing ? '' : '-'}
                                        </FormParagraph>
                                    )}
                                </Col>
                            </>
                        )}
                    </Row>
                    {errors.get(`availabilities.${day}.${period}`) && (
                        <Row>
                            <Col span={12}>
                                <FormErrorMessage>
                                    {getValidationErrorTranslation(errors.get(`availabilities.${day}.${period}`))}
                                </FormErrorMessage>
                            </Col>
                        </Row>
                    )}
                    {!isEditing && !availability.enabled && <FormParagraph>-</FormParagraph>}
                </FormGroup>
            )
        );
    }

    async function loadAvailabilities() {
        const result = await fetchJson(
            `/api/availabilities?employmentId=${props.employmentId}&availabilityType=${AvailabilityType.phone}&includeListAuthorizations=canAdd&includeItemAuthorizations=canUpdate,canDelete`,
            'GET'
        );
        const currentAvailabilities: IAvailability[] = result.json.items;
        setCanAdd(result.json.authorization.canAdd);

        let availabilitiesByDay: IAvailabilitiesByDay = {};

        BusinessDays.forEach((day) => {
            const defaultAvailabilityPeriodOne = {
                availabilityType: AvailabilityType.phone,
                dayOfWeek: day,
                startTime: defaultStartTimePeriodOne,
                endTime: defaultEndTimePeriodOne,
                enabled: false,
            };
            const defaultAvailabilityPeriodTwo = {
                availabilityType: AvailabilityType.phone,
                dayOfWeek: day,
                startTime: defaultStartTimePeriodTwo,
                endTime: defaultEndTimePeriodTwo,
                enabled: false,
            };

            const [currentPeriodOne, currentPeriodTwo] = currentAvailabilities
                .filter((availability) => availability.dayOfWeek === day)
                .sort((a, b) =>
                    DateTime.parse(a.startTime!, 'HH:mm:ss').isBefore(DateTime.parse(b.startTime!, 'HH:mm:ss')) ? -1 : 1
                )
                .map((availability) => ({
                    ...availability,
                    startTime: DateTime.parse(availability.startTime!, 'HH:mm:ss').format('HH:mm'),
                    endTime: DateTime.parse(availability.endTime!, 'HH:mm:ss').format('HH:mm'),
                }));

            availabilitiesByDay = {
                ...availabilitiesByDay,
                [day]: {
                    periodOne: currentPeriodOne ? currentPeriodOne : defaultAvailabilityPeriodOne,
                    periodTwo: currentPeriodTwo ? currentPeriodTwo : defaultAvailabilityPeriodTwo,
                },
            };
        });

        setAvailabilities(availabilitiesByDay);
        setOriginalAvailabilities(availabilitiesByDay);
    }

    function handleChange(
        day: DayOfWeek,
        period: 'periodOne' | 'periodTwo',
        updatedFields: Partial<IAvailability>
    ): IAvailabilitiesByDay {
        if (availabilities) {
            const updatedAvailabilities = {
                ...availabilities,
                [day]: {
                    ...availabilities[day],
                    [period]: {
                        ...availabilities[day][period],
                        ...updatedFields,
                    },
                },
            };
            setAvailabilities(updatedAvailabilities);

            return updatedAvailabilities;
        }

        return {};
    }

    function handleTimeInputBlur(
        day: DayOfWeek,
        period: 'periodOne' | 'periodTwo',
        timeField: keyof Pick<IAvailability, 'startTime' | 'endTime'>,
        value: string
    ) {
        const parsedTime = parseTime(value);
        const updatedAvailabilities = handleChange(day, period, { [timeField]: parsedTime });
        validateOneTimeField(updatedAvailabilities, day, period, timeField);
    }

    function parseTime(time: string) {
        const allowedInputFormats = [
            'HHmm',
            'Hmm',
            'HHm',
            'HH',
            'Hm',
            'H',
            'HH:mm',
            'H:mm',
            'HH:m',
            'H:m',
            'HH.mm',
            'H.mm',
            'HH.m',
            'H.m',
        ];
        const parsed = DateTime.parse(time, allowedInputFormats);

        return parsed.isValid() ? parsed.format('HH:mm') : '';
    }

    function validateOneTimeField(
        updatedAvailabilities: IAvailabilitiesByDay,
        day: DayOfWeek,
        period: 'periodOne' | 'periodTwo',
        timeField: 'startTime' | 'endTime'
    ) {
        if (!updatedAvailabilities) {
            return;
        }

        setErrors(
            updateValidationErrorsForOne(
                availabilityAddEditSchema,
                updatedAvailabilities[day][period],
                errors,
                timeField,
                `availabilities.${day}.${period}.${timeField}`
            )
        );
    }

    function toggleTimePeriodEnabled(day: DayOfWeek, period: 'periodOne' | 'periodTwo', enabled: boolean) {
        if (availabilities && availabilities[day][period].enabled !== enabled) {
            handleChange(day, period, {
                enabled,
                startTime: period === 'periodOne' ? defaultStartTimePeriodOne : defaultStartTimePeriodTwo,
                endTime: period === 'periodOne' ? defaultEndTimePeriodOne : defaultEndTimePeriodTwo,
            });

            if (!enabled) {
                let validationErrors = errors;
                const errorKeysForDay = errors
                    .keySeq()
                    .filter((key) => Boolean(key?.startsWith(`availabilities.${day}`)))
                    .toArray();
                for (const key of errorKeysForDay) {
                    validationErrors = validationErrors.remove(key);
                }
                setErrors(validationErrors);
            }
        }
    }

    function validateTimePeriods() {
        if (!availabilities) {
            return errors;
        }

        let validationErrors = errors;

        for (const day of BusinessDays) {
            const availability = availabilities[day];

            validationErrors = validateAvailabilityPeriod(
                availability.periodOne,
                validationErrors,
                `availabilities.${day}.periodOne`
            );
            validationErrors = validateAvailabilityPeriod(
                availability.periodTwo,
                validationErrors,
                `availabilities.${day}.periodTwo`
            );
            validationErrors = validateAvailabilityPeriodOverlap(
                availability,
                validationErrors,
                `availabilities.${day}`
            );
        }
        setErrors(validationErrors);

        return validationErrors;
    }

    function handleCancel() {
        setAvailabilities(originalAvailabilities);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    function removePeriodErrors(day: DayOfWeek) {
        const newErrors = errors
            .filterNot((_value, key) => Boolean(key && key.match(RegExp(`^availabilities.${day}`))))
            .toMap();
        setErrors(newErrors);
    }

    async function handleSubmit() {
        const validationErrors = validateTimePeriods();

        if (validationErrors.size > 0) {
            setScrollToErrorSignalCounter((counter) => counter + 1);
        } else if (availabilities) {
            setIsSaving(true);

            for (const periods of Object.values(availabilities)) {
                for (const availability of Object.values(periods)) {
                    if (availability.id && !availability.enabled && availability.authorization?.canDelete) {
                        await fetchJson(`/api/availabilities/${availability.id}`, 'DELETE');
                    } else if (availability.enabled) {
                        const { item } = validate(availabilityAddEditSchema, availability);

                        if (availability.id && availability.authorization?.canUpdate) {
                            await fetchJson(`/api/availabilities/${availability.id}`, 'POST', item);
                        } else if (canAdd) {
                            if (item) {
                                item.employmentId = props.employmentId;
                            }
                            await fetchJson('/api/availabilities', 'POST', item);
                        }
                    }
                }
            }

            setIsEditing(false);
            loadAvailabilities();
            setIsSaving(false);
        }
    }
}
